import React from 'react';
import { Button } from 'reactstrap';
import PropTypes from 'prop-types';
import { GLOBAL_BUTTON_SIZE } from './CButton';

const IconButton = ({
  icon,
  color = "primary",
  size = "sm", // sm, md, lg
  variant = "default", // default, rounded-circle (always has background)
  disabled = false,
  loading = false,
  className = "",
  onClick,
  style = {},
  title,
  'data-bs-placement': dataBsPlacement = 'top',
  ...buttonProps
}) => {
  // Icon size remains constant regardless of button size
  const iconClass = icon || "bx bx-help";

  // Handle loading state with consistent spinner size
  const loadingElement = loading && (
    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
  );

  // Always use solid background colors (no outline or soft variants)
  const getButtonColor = () => {
    return color; // Always solid background
  };

  // Build className
  const buildClassName = () => {
    let classes = [];
    
    // Add base classes
    if (className) classes.push(className);
    
    // Add rounded-circle for circular buttons
    if (variant === 'rounded-circle') {
      classes.push('rounded-circle');
    }
    
    // Add wave effects for interactive buttons
    if (!disabled && !loading) {
      classes.push('waves-effect waves-light');
    }
    
    return classes.join(' ');
  };

  // Build style for consistent icon button sizing
  const buildStyle = () => {
    const baseStyle = { ...style };
    
    if (variant === 'rounded-circle') {
      // Circular buttons have fixed dimensions based on size
      switch (size) {
        case 'sm':
          return { width: '32px', height: '32px', padding: '0', ...baseStyle };
        case 'lg':
          return { width: '48px', height: '48px', padding: '0', ...baseStyle };
        default: // md
          return { width: '40px', height: '40px', padding: '0', ...baseStyle };
      }
    }
    
    // Regular icon buttons have consistent padding
    return baseStyle;
  };

  return (
    <Button
      color={getButtonColor()}
      size={size}
      disabled={disabled || loading}
      className={buildClassName()}
      onClick={onClick}
      style={buildStyle()}
      title={title}
      data-bs-placement={dataBsPlacement}
      {...buttonProps}
    >
      {loading ? loadingElement : <i className={iconClass}></i>}
    </Button>
  );
};

IconButton.propTypes = {
  icon: PropTypes.string.isRequired,
  color: PropTypes.oneOf([
    'primary', 'secondary', 'success', 'danger', 'warning', 'info', 'light', 'dark'
  ]),
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  variant: PropTypes.oneOf(['default', 'rounded-circle']),
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  className: PropTypes.string,
  onClick: PropTypes.func,
  style: PropTypes.object,
  title: PropTypes.string,
  'data-bs-placement': PropTypes.oneOf(['top', 'bottom', 'left', 'right']),
};

// Predefined icon-only button configurations (always with background)
const EditIconButton = (props) => (
  <IconButton
    icon="bx bx-edit"
    color="primary"
    title="Edit"
    {...props}
  />
);
EditIconButton.displayName = 'IconButton.Edit';

const DeleteIconButton = (props) => (
  <IconButton
    icon="bx bx-trash"
    color="danger"
    title="Delete"
    {...props}
  />
);
DeleteIconButton.displayName = 'IconButton.Delete';

const ViewIconButton = (props) => (
  <IconButton
    icon="mdi mdi-eye"
    color="info"
    title="View"
    {...props}
  />
);
ViewIconButton.displayName = 'IconButton.View';

const SaveIconButton = (props) => (
  <IconButton
    icon="bx bx-check"
    color="success"
    variant="rounded-circle"
    title="Save"
    {...props}
  />
);
SaveIconButton.displayName = 'IconButton.Save';

const HistoryIconButton = (props) => (
  <IconButton
    icon="mdi mdi-history"
    color="secondary"
    title="History"
    {...props}
  />
);
HistoryIconButton.displayName = 'IconButton.History';

const CancelIconButton = (props) => (
  <IconButton
    icon="bx bx-x"
    color="danger"
    variant="rounded-circle"
    title="Cancel"
    {...props}
  />
);
CancelIconButton.displayName = 'IconButton.Cancel';

const AddIconButton = (props) => (
  <IconButton
    icon="bx bx-plus"
    color="success"
    title="Add"
    {...props}
  />
);
AddIconButton.displayName = 'IconButton.Add';

const PostIconButton = (props) => (
  <IconButton
    icon="mdi mdi-send"
    color="primary"
    title="Post"
    {...props}
  />
);
PostIconButton.displayName = 'IconButton.Post';

const ProcessIconButton = (props) => (
  <IconButton
    icon="mdi mdi-check"
    color="success"
    title="Process"
    {...props}
  />
);
ProcessIconButton.displayName = 'IconButton.Process';

const RejectIconButton = (props) => (
  <IconButton
    icon="mdi mdi-close"
    color="danger"
    title="Reject"
    {...props}
  />
);
RejectIconButton.displayName = 'IconButton.Reject';

const PublishIconButton = (props) => (
  <IconButton
    icon="mdi mdi-publish"
    color="success"
    title="Publish"
    {...props}
  />
);
PublishIconButton.displayName = 'IconButton.Publish';

const DenyIconButton = (props) => (
  <IconButton
    icon="mdi mdi-cancel"
    color="danger"
    title="Deny"
    {...props}
  />
);
DenyIconButton.displayName = 'IconButton.Deny';

// Attach to IconButton
IconButton.Edit = EditIconButton;
IconButton.Delete = DeleteIconButton;
IconButton.View = ViewIconButton;
IconButton.Save = SaveIconButton;
IconButton.Cancel = CancelIconButton;
IconButton.Add = AddIconButton;
IconButton.History = HistoryIconButton;
IconButton.Post = PostIconButton;
IconButton.Process = ProcessIconButton;
IconButton.Reject = RejectIconButton;
IconButton.Publish = PublishIconButton;
IconButton.Deny = DenyIconButton;

export default IconButton;